// All new components must import useTranslation and use t() for UI strings
import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../auth/AuthProvider';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import UserAvatar from '../../components/ui/UserAvatar';
import useTranslation from '../../hooks/useTranslation';
import { useLanguage } from '../../contexts/LanguageContext';
import { supabase } from '../../lib/supabase';

// ── Canvas resize helper — no new dependency ──────────────────────────────────
const resizeImageToCanvas = (file, maxPx = 512) => {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { width, height } = img;
      const scale = Math.min(1, maxPx / Math.max(width, height));
      const w = Math.round(width * scale);
      const h = Math.round(height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => resolve(blob || file),
        file.type === 'image/png' ? 'image/png' : 'image/jpeg',
        0.85
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
};

const UserProfile = () => {
  const { userProfile, updateProfile, profileLoading, refreshProfile } = useAuth();
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    role: '',
    company: ''
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');

  // Avatar state
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarToast, setAvatarToast] = useState(null); // { message, type }
  const avatarInputRef = useRef(null);

  // Load profile data when available
  useEffect(() => {
    if (userProfile) {
      setFormData({
        full_name: userProfile?.full_name || '',
        email: userProfile?.email || '',
        phone: userProfile?.phone || '',
        role: userProfile?.role || '',
        company: userProfile?.company || ''
      });
      setAvatarUrl(userProfile?.logo_url || null);
    }
  }, [userProfile]);

  const showAvatarToast = (message, type = 'success') => {
    setAvatarToast({ message, type });
    setTimeout(() => setAvatarToast(null), 3500);
  };

  const handleAvatarClick = () => {
    avatarInputRef?.current?.click();
  };

  const handleAvatarFileChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes?.includes(file?.type)) {
      showAvatarToast(t('profile.avatar.invalidType'), 'error');
      if (avatarInputRef?.current) avatarInputRef.current.value = '';
      return;
    }

    // Validate size (2 MB)
    if (file?.size > 2 * 1024 * 1024) {
      showAvatarToast(t('profile.avatar.tooLarge'), 'error');
      if (avatarInputRef?.current) avatarInputRef.current.value = '';
      return;
    }

    setAvatarUploading(true);
    const previousUrl = avatarUrl;

    try {
      const userId = userProfile?.id;

      // Resize to max 512×512 using canvas (no new dependency)
      const resized = await resizeImageToCanvas(file, 512);

      // Determine extension
      const ext = file?.type === 'image/png' ? 'png' : file?.type === 'image/webp' ? 'webp' : 'jpg';
      const filePath = `${userId}/avatar.${ext}`;

      // Upload to avatars bucket (upsert = overwrite)
      const { error: uploadError } = await supabase?.storage
        ?.from('avatars')
        ?.upload(filePath, resized, { upsert: true, cacheControl: '3600', contentType: file?.type });

      if (uploadError) throw uploadError;

      // Get public URL + cache-bust
      const { data: urlData } = supabase?.storage?.from('avatars')?.getPublicUrl(filePath);
      const publicUrl = `${urlData?.publicUrl}?v=${Date.now()}`;

      // Optimistic UI update
      setAvatarUrl(publicUrl);

      // Persist to user_profiles.logo_url
      const { error: profileError } = await supabase
        ?.from('user_profiles')
        ?.update({ logo_url: publicUrl })
        ?.eq('id', userId);

      if (profileError) throw profileError;

      // Refresh auth context so GlobalHeader + sidebar update immediately
      await refreshProfile?.();

      showAvatarToast(t('profile.avatar.uploadSuccess'), 'success');
    } catch (err) {
      console.error('Avatar upload error:', err);
      setAvatarUrl(previousUrl); // revert optimistic update
      showAvatarToast(t('profile.avatar.uploadFailed'), 'error');
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef?.current) avatarInputRef.current.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    const userId = userProfile?.id;
    const previousUrl = avatarUrl;
    setAvatarUrl(null); // optimistic

    try {
      const { error: profileError } = await supabase
        ?.from('user_profiles')
        ?.update({ logo_url: null })
        ?.eq('id', userId);

      if (profileError) throw profileError;

      await refreshProfile?.();
    } catch (err) {
      console.error('Remove avatar error:', err);
      setAvatarUrl(previousUrl); // revert
      showAvatarToast(t('profile.avatar.uploadFailed'), 'error');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    setError('');
    
    try {
      const { data, error } = await updateProfile({
        full_name: formData?.full_name,
        email: formData?.email,
        phone: formData?.phone,
        company: formData?.company
      });

      if (error) {
        setError(error?.message || 'Failed to update profile');
      } else {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-sm text-muted-foreground">{t('profile.loadingProfile')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>My Profile - OnSitePm</title>
      </Helmet>

      {/* Avatar toast */}
      {avatarToast && (
        <div className={`fixed top-4 right-4 z-[10000] flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium border ${
          avatarToast?.type === 'error' ?'bg-red-50 border-red-200 text-red-700' :'bg-green-50 border-green-200 text-green-700'
        }`}>
          <Icon name={avatarToast?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={16} />
          <span>{avatarToast?.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground">{t('profile.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('profile.subtitle')}</p>
      </div>

      {/* Profile Form Card */}
      <div className="max-w-2xl">
        <div className="bg-card rounded-lg border border-border p-6">
          {/* Avatar Section */}
          <div className="flex items-center space-x-4 mb-6 pb-6 border-b border-border">
            {/* Clickable avatar with hover overlay */}
            <div className="relative group flex-shrink-0">
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleAvatarFileChange}
              />
              <button
                onClick={handleAvatarClick}
                disabled={avatarUploading}
                className="relative w-20 h-20 rounded-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2"
                title={t('profile.avatar.editPhoto')}
              >
                <UserAvatar
                  user={{ ...userProfile, logo_url: avatarUrl }}
                  size={80}
                  className="w-20 h-20"
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/50 rounded-full flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  {avatarUploading ? (
                    <Icon name="Loader2" size={18} className="text-white animate-spin" />
                  ) : (
                    <>
                      <Icon name="Camera" size={18} className="text-white" />
                      <span className="text-[10px] text-white font-medium mt-0.5 leading-tight text-center px-1">
                        {t('profile.avatar.editPhoto')}
                      </span>
                    </>
                  )}
                </div>
              </button>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#2C3E50]">{formData?.full_name || 'User'}</h2>
              <p className="text-sm font-normal text-[#6B7280]">{formData?.email}</p>
              {avatarUrl && (
                <button
                  onClick={handleRemoveAvatar}
                  className="mt-1 text-xs text-red-500 hover:text-red-700 transition-colors underline"
                >
                  {t('profile.avatar.removePhoto')}
                </button>
              )}
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            <Input
              label={t('profile.fullNameLabel')}
              type="text"
              value={formData?.full_name}
              onChange={(e) => handleInputChange('full_name', e?.target?.value)}
              placeholder={t('profile.fullNamePlaceholder')}
            />

            <Input
              label={t('profile.emailLabel')}
              type="email"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              placeholder={t('profile.emailPlaceholder')}
            />

            <Input
              label={t('profile.phoneLabel')}
              type="tel"
              value={formData?.phone}
              onChange={(e) => handleInputChange('phone', e?.target?.value)}
              placeholder={t('profile.phonePlaceholder')}
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('profile.roleLabel')}
              </label>
              <div className="inline-flex items-center px-3 py-2 rounded-md bg-primary/10 border border-primary/20">
                <Icon name="Briefcase" size={16} className="text-primary mr-2" />
                <span className="text-sm font-medium text-primary">{formData?.role || t('profile.roleNotSet')}</span>
              </div>
              <p className="text-sm text-[#6B7280] mt-1">{t('profile.roleReadOnly')}</p>
            </div>

            <Input
              label={t('profile.companyLabel')}
              type="text"
              value={formData?.company}
              onChange={(e) => handleInputChange('company', e?.target?.value)}
              placeholder={t('profile.companyPlaceholder')}
            />
          </div>

          {/* Language Setting Row */}
          <div className="pt-4 border-t border-border">
            <label className="block text-sm font-medium text-foreground mb-2">
              Langue / Language
            </label>
            <div
              className="inline-flex items-center"
              style={{
                background: '#F1F3F5',
                border: '1px solid #E5E7EB',
                borderRadius: '999px',
                padding: '3px',
                height: '32px',
                gap: '0',
              }}
            >
              <button
                onClick={() => setLanguage('fr')}
                style={{
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  lineHeight: 1,
                  padding: '0 14px',
                  height: '24px',
                  borderRadius: '999px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background 160ms ease, color 160ms ease',
                  background: language === 'fr' ? '#FF6B2C' : 'transparent',
                  color: language === 'fr' ? '#ffffff' : '#6B7280',
                }}
                onMouseEnter={e => { if (language !== 'fr') e.currentTarget.style.color = '#374151'; }}
                onMouseLeave={e => { if (language !== 'fr') e.currentTarget.style.color = '#6B7280'; }}
              >
                FR
              </button>
              <button
                onClick={() => setLanguage('en')}
                style={{
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  lineHeight: 1,
                  padding: '0 14px',
                  height: '24px',
                  borderRadius: '999px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background 160ms ease, color 160ms ease',
                  background: language === 'en' ? '#FF6B2C' : 'transparent',
                  color: language === 'en' ? '#ffffff' : '#6B7280',
                }}
                onMouseEnter={e => { if (language !== 'en') e.currentTarget.style.color = '#374151'; }}
                onMouseLeave={e => { if (language !== 'en') e.currentTarget.style.color = '#6B7280'; }}
              >
                EN
              </button>
            </div>
            <p className="text-xs text-[#6B7280] mt-1.5">
              {language === 'fr' ? 'Langue d\'affichage de l\'application' : 'Application display language'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 flex items-center space-x-2 p-3 rounded-md bg-red-50 border border-red-200">
              <Icon name="AlertCircle" size={16} className="text-red-600" />
              <span className="text-sm text-red-600">{error}</span>
            </div>
          )}

          {/* Save Button */}
          <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-border">
            {saveSuccess && (
              <div className="flex items-center space-x-2 text-green-600">
                <Icon name="CheckCircle" size={16} />
                <span className="text-sm font-medium">{t('profile.saveSuccess')}</span>
              </div>
            )}
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center space-x-2"
            >
              {isSaving ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  <span>{t('profile.saving')}</span>
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} />
                  <span>{t('profile.saveChanges')}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;